import { useActiveContext } from '@texas/hooks/useActiveContext';
import { AiAgentContext } from '../useAgentSession';
import { Box, SkeletonText } from '@chakra-ui/react';
import { useActiveSession } from '@texas/hooks/useSession';
import { markdownCssStyle } from '../markdownStyle';
import { useCallback, useEffect, useRef } from 'react';
import { compareEnum } from '@texas/utils/helpers/enumHelpers';
import { ChatItemType } from '@texas/api/endpoints/chat/aiMessageTypes';
import { ChatMessage } from './ChatMessage';
import { JobMessage } from './JobMessage';
import { clamp } from '@texas/utils/helpers/numberHelpers';

interface Props {
  scrollOnMessages: boolean;
}

export function ChatContainer(props: Props) {
  const { history, threadLoading, messages, jobs } =
    useActiveContext(AiAgentContext);
  const session = useActiveSession();

  const containerRef = useRef<HTMLDivElement | null>(null);

  const scrollIntoView = useCallback((behavior: ScrollBehavior) => {
    if (!containerRef.current || containerRef.current.children.length === 0) {
      return;
    }
    const clamped = clamp(
      containerRef.current.children.length - 1,
      0,
      containerRef.current.children.length,
    );

    const lastEl = containerRef.current.children[clamped];

    lastEl.scrollIntoView({ behavior });
  }, []);

  useEffect(() => {
    if (history.scroll && props.scrollOnMessages) {
      scrollIntoView(history.scroll);
    }
  }, [history, props.scrollOnMessages, scrollIntoView]);

  return (
    <Box
      flexDir="column"
      display="flex"
      gap={4}
      w="full"
      maxW="800px"
      marginX="auto"
      __css={markdownCssStyle}
      ref={containerRef}
    >
      {threadLoading && (
        <SkeletonText mt="4" noOfLines={4} spacing="4" skeletonHeight="3" />
      )}
      {history.messages.map((h, i) => {
        if (compareEnum(ChatItemType, h.type, ChatItemType.Message)) {
          const msg = messages.current[h.id];
          return <ChatMessage key={i} message={msg} session={session} />;
        } else if (compareEnum(ChatItemType, h.type, ChatItemType.Job)) {
          const job = jobs.current[h.id];
          return <JobMessage key={i} job={job} />;
        }
      })}
    </Box>
  );
}
